import { Injectable } from '@angular/core';
import {
  CRMStatus,
  CRMStatusLabels,
  CRMStatusToStatusOptions,
  DL_APPROVES_INVESTMENT_STATUSES,
  PENDING_STATUSES
} from '../../shared/models/crm/CRMStatus';
import { CRMItem } from '../../shared/models/crm/CRMItem.model';
import { CrmUtilsService } from './crm-utils.service';
import { catchError, shareReplay } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AbstractCRUD, ExceptionService, UtilsService } from '../../xform-compat';
import { BasicListModel } from '../../shared/models';
import { HttpClient } from '@angular/common/http';
import { CrmUserService } from './crm-user.service';
import { StructCapCRMStatus } from '../../shared/enums/struct-cap-crm-status';

@Injectable({
  providedIn: 'root'
})
export class CrmStatusService extends AbstractCRUD<BasicListModel> {

  private statusOrderCache$: Observable<Array<CRMStatus>>;
  private crmUserService: any;

  private nonLeadStatuses = [
    CRMStatus.RECOMMEND_TAKE_MEETING,
    CRMStatus.RECOMMEND_PASS_ON_MEETING,
    CRMStatus.RESEARCH,
    CRMStatus.DISCUSSION,
    CRMStatus.WATCHFUL_WAITING,
    CRMStatus.RECOMMEND_INVEST_BUY,
    CRMStatus.RECOMMEND_INVEST_SELL,
    CRMStatus.RECOMMEND_INVEST_SHORT,
    CRMStatus.RECOMMEND_INVEST_COVER,
    CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION,
    CRMStatus.RECOMMEND_NO_INVESTMENT,
  ];

  private leadStatuses = [
    CRMStatus.DL_REJECTS_RECOMMEND_NO_INVESTMENT,
    CRMStatus.DL_REJECTS_RECOMMEND_AWAITING_KEY_INFLECTION,
    CRMStatus.DL_REJECTS_RECOMMEND_INVESTMENT,
    CRMStatus.DL_APPROVES_RECOMMEND_NO_INVESTMENT,
    CRMStatus.DL_APPROVES_RECOMMEND_AWAITING_KEY_INFLECTION,
    ...DL_APPROVES_INVESTMENT_STATUSES,
  ];

  private pmStatuses = [
    CRMStatus.PM_REJECTS_RECOMMEND_INVESTMENT,
    CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT,
    CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_BUY,
    CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SELL,
    CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_COVER,
    CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SHORT,
  ];

  public crmProjectStatus = [
    CRMStatus.RESEARCH,
    CRMStatus.DISCUSSION,
  ];

  // Statuses that are no longer selectable in RMS. Triage is tracked in Notion, so the triage
  // statuses are hidden from the status dropdown while remaining valid for records that still
  // carry them.
  private hiddenStatuses = [
    CRMStatus.RECOMMEND_TAKE_MEETING,
    CRMStatus.RECOMMEND_PASS_ON_MEETING,
  ];

  constructor(
    exceptionService: ExceptionService,
    http: HttpClient,
    utilsService: UtilsService,
    private crmUtilsService: CrmUtilsService,
    crmUserService: CrmUserService,
  ) {
    super(exceptionService, http, utilsService);
    this.crmUserService = crmUserService;
  }

  get statusOrder() {
    if (!this.statusOrderCache$) {
      this.statusOrderCache$ = this.loadCrmStatusOrder().pipe(shareReplay(1));
    }
    return this.statusOrderCache$;
  }

  public getResourcePath(): string {
    return '/crm/status';
  }

  private getAvailableStatuses(): Array<CRMStatus> {
    if (this.crmUserService.isPortfolioManager()) {
      return [
        ...this.nonLeadStatuses,
        ...this.pmStatuses,
        CRMStatus.CLOSED_BEFORE_DECISION,
      ];
    }
    if (this.crmUserService.isDomainLead()) {
      return [
        ...this.nonLeadStatuses,
        ...this.leadStatuses,
        CRMStatus.CLOSED_BEFORE_DECISION,
      ];
    }
    if (this.crmUserService.isCrmUser()) {
      return [
        ...this.nonLeadStatuses,
        CRMStatus.CLOSED_BEFORE_DECISION,
      ];
    }
    return [];
  }

  public getUserPMStatus(): Array<CRMStatus> {
    return this.crmUserService.isPortfolioManager() ?
      [
        ...DL_APPROVES_INVESTMENT_STATUSES,
        CRMStatus.DL_APPROVES_RECOMMEND_AWAITING_KEY_INFLECTION,
        CRMStatus.DL_APPROVES_RECOMMEND_NO_INVESTMENT,
      ] : [];
  }

  public getUserDLStatus(): Array<CRMStatus> {
    return this.crmUserService.isDomainLead() ?
      [
        CRMStatus.RECOMMEND_NO_INVESTMENT,
        CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION,
        CRMStatus.RECOMMEND_INVEST_BUY,
        CRMStatus.RECOMMEND_INVEST_SELL,
        CRMStatus.RECOMMEND_INVEST_SHORT,
        CRMStatus.RECOMMEND_INVEST_COVER,
      ] : [];
  }


  public userCanApproveItem(crmItem: CRMItem): boolean {
    const isPM = this.crmUserService.isPortfolioManager(crmItem);
    const isDL = this.crmUserService.isDomainLead(crmItem);

    return PENDING_STATUSES.includes(crmItem.status) &&
      (isPM || (isDL && DL_APPROVES_INVESTMENT_STATUSES.every(s => crmItem.status !== s)));
  }

  public approveItem(crmItem: CRMItem) {
    const isPM = this.crmUserService.isPortfolioManager(crmItem);
    const isDL = this.crmUserService.isDomainLead(crmItem);

    if (isPM) {
      switch (crmItem.status) {
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT:
          crmItem.status = CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT;
          break;
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_BUY:
          crmItem.status = CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_BUY;
          break;
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SELL:
          crmItem.status = CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SELL;
          break;
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_COVER:
          crmItem.status = CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_COVER;
          break;
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SHORT:
          crmItem.status = CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SHORT;
          break;
      }
    }

    if (isDL || isPM) {
      switch (crmItem.status) {
        case CRMStatus.RECOMMEND_INVEST_BUY:
          crmItem.status = CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_BUY;
          break;
        case CRMStatus.RECOMMEND_INVEST_SELL:
          crmItem.status = CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SELL;
          break;
        case CRMStatus.RECOMMEND_INVEST_SHORT:
          crmItem.status = CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SHORT;
          break;
        case CRMStatus.RECOMMEND_INVEST_COVER:
          crmItem.status = CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_COVER;
          break;
        case CRMStatus.RECOMMEND_NO_INVESTMENT:
          crmItem.status = CRMStatus.DL_APPROVES_RECOMMEND_NO_INVESTMENT;
          break;

        case CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION:
          crmItem.status = CRMStatus.DL_APPROVES_RECOMMEND_AWAITING_KEY_INFLECTION;
          break;
      }
    }
  }

  public rejectItem(crmItem: CRMItem) {
    const isPM = this.crmUserService.isPortfolioManager(crmItem);
    const isDL = this.crmUserService.isDomainLead(crmItem);
    if (isDL || isPM) {
      switch (crmItem.status) {
        case CRMStatus.RECOMMEND_INVEST_BUY:
        case CRMStatus.RECOMMEND_INVEST_SELL:
        case CRMStatus.RECOMMEND_INVEST_SHORT:
        case CRMStatus.RECOMMEND_INVEST_COVER:
          crmItem.status = CRMStatus.DL_REJECTS_RECOMMEND_INVESTMENT;
          break;

        case CRMStatus.RECOMMEND_NO_INVESTMENT:
          crmItem.status = CRMStatus.DL_REJECTS_RECOMMEND_NO_INVESTMENT;
          break;

        case CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION:
          crmItem.status = CRMStatus.DL_REJECTS_RECOMMEND_AWAITING_KEY_INFLECTION;
          break;
      }
    }

    if (isPM) {
      switch (crmItem.status) {
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT:
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_BUY:
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SELL:
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_COVER:
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SHORT:
          crmItem.status = CRMStatus.PM_REJECTS_RECOMMEND_INVESTMENT;
          break;
      }
    }
  }

  public getStatusDropdownOptions(crmItem?: CRMItem, isCrmProject?: boolean): Array<{name: CRMStatus, group: string}>  {
    if (isCrmProject) {
      return this.groupStatuses(this.crmProjectStatus);
    }
    if (crmItem) {
      const status = crmItem.status;
      if (!status) {
        return;
      }
      if (this.crmUtilsService.isCrmProject(crmItem)) {
        return this.groupStatuses(this.crmProjectStatus);
      }
      const isPM = this.crmUserService.isPortfolioManager(crmItem);
      const isDL = this.crmUserService.isDomainLead(crmItem);
      let statuses: Array<CRMStatus> = this.removeHiddenStatuses(CRMStatusToStatusOptions[status]);
      if (!isDL) {
        statuses = statuses.filter(value => !this.leadStatuses.includes(value));
      }
      if (!isPM) {
        statuses = statuses.filter(value => !this.pmStatuses.includes(value));
      }
      if (!statuses.includes(status)) {
        statuses.push(status);
      }
      return this.groupStatuses(statuses);
    }
    return this.groupStatuses(this.removeHiddenStatuses(this.getAvailableStatuses()));
  }

  private groupStatuses(statuses: Array<CRMStatus>): Array<{name: CRMStatus, group: string}> {
    return statuses.map(s => ({
      name: s,
      group: 'Sonar Status'
    }));
  }

  /**
   * Returns a copy of the given statuses with the no-longer-selectable statuses removed so they
   * do not appear as dropdown options. Callers that need to keep a record's existing status
   * visible must re-add it after filtering.
   */
  private removeHiddenStatuses(statuses: Array<CRMStatus>): Array<CRMStatus> {
    return statuses.filter(s => !this.hiddenStatuses.includes(s));
  }

  public isSilenceStatus(status: CRMStatus): boolean {
    const silenceStatuses = [
      CRMStatus.RESEARCH,
      CRMStatus.DISCUSSION,
      CRMStatus.WATCHFUL_WAITING,
      CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION,
      CRMStatus.DL_APPROVES_RECOMMEND_AWAITING_KEY_INFLECTION,
      CRMStatus.SILENCED
    ];
    return silenceStatuses.includes(status);
  }

  public getStatusColor(status: CRMStatus | StructCapCRMStatus): string {
    if (status === CRMStatus.RESEARCH ||
      status === CRMStatus.DISCUSSION ||
      status === CRMStatus.UNSILENCED ||
      status === CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION ||
      status === CRMStatus.RECOMMEND_INVEST_BUY ||
      status === CRMStatus.RECOMMEND_INVEST_SELL ||
      status === CRMStatus.RECOMMEND_INVEST_SHORT ||
      status === CRMStatus.RECOMMEND_INVEST_COVER ||
      status === CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT ||
      status === CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_BUY ||
      status === CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SELL ||
      status === CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_COVER ||
      status === CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SHORT ||
      status === CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT ||
      status === CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_BUY ||
      status === CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SELL ||
      status === CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_COVER ||
      status === CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SHORT ||
      status === CRMStatus.INVESTMENT_MADE_OPTIMAL ||
      status === CRMStatus.INVESTMENT_MADE_SUBOPTIMAL ||
      status === StructCapCRMStatus.INITIAL_INBOUND ||
      status === StructCapCRMStatus.NDA_IN_PROCESS ||
      status === StructCapCRMStatus.EARLY_REVIEW ||
      status === StructCapCRMStatus.DEAL_TEAM_WORKING_UP ||
      status === StructCapCRMStatus.TERM_SHEET_SENT ||
      status === StructCapCRMStatus.TERM_SHEET_SIGNED ||
      status === StructCapCRMStatus.CLOSED) {
      return 'assets/icons/sonar-status-green.svg';
    } else if (status === CRMStatus.RECOMMEND_NO_INVESTMENT ||
      status === CRMStatus.DL_APPROVES_RECOMMEND_AWAITING_KEY_INFLECTION ||
      status === CRMStatus.WATCHFUL_WAITING ||
      status === CRMStatus.SILENCED ||
      status === CRMStatus.RECOMMEND_TAKE_MEETING ||
      status === StructCapCRMStatus.ON_HOLD) {
      return 'assets/icons/sonar-status-yellow.svg';
    } else {
      return 'assets/icons/sonar-status-red.svg';
    }
  }

  public getCondensedStatuses(statuses: Array<CRMStatus>): Array<string> {
    const condensedSet = new Set<string>();
    statuses.forEach(status => {
      switch (status) {
        case CRMStatus.RECOMMEND_INVEST_BUY:
        case CRMStatus.RECOMMEND_INVEST_SELL:
        case CRMStatus.RECOMMEND_INVEST_SHORT:
        case CRMStatus.RECOMMEND_INVEST_COVER:
        case CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION:
        case CRMStatus.RECOMMEND_NO_INVESTMENT:
          condensedSet.add('Recommend');
          break;
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT:
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_BUY:
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SELL:
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_COVER:
        case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SHORT:
        case CRMStatus.DL_APPROVES_RECOMMEND_AWAITING_KEY_INFLECTION:
        case CRMStatus.DL_APPROVES_RECOMMEND_NO_INVESTMENT:
          condensedSet.add('DL Approves');
          break;
        case CRMStatus.DL_REJECTS_RECOMMEND_INVESTMENT:
        case CRMStatus.DL_REJECTS_RECOMMEND_AWAITING_KEY_INFLECTION:
        case CRMStatus.DL_REJECTS_RECOMMEND_NO_INVESTMENT:
          condensedSet.add('DL Rejects');
          break;
        case CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT:
        case CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_BUY:
        case CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SELL:
        case CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_COVER:
        case CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SHORT:
          condensedSet.add('PM Approves');
          break;
        case CRMStatus.PM_REJECTS_RECOMMEND_INVESTMENT:
          condensedSet.add('PM Rejects');
          break;
        case CRMStatus.RECOMMEND_TAKE_MEETING:
          condensedSet.add('Recommend Take Meeting');
          break;
        case CRMStatus.RECOMMEND_PASS_ON_MEETING:
          condensedSet.add('Recommend Pass on Meeting');
          break;
        default:
          condensedSet.add(CRMStatusLabels[status]);
      }
    });
    return Array.from(condensedSet);
  }

  public convertCondensedStatuses(filterCondensedStatuses: Array<string>): Array<CRMStatus> {
    const statusSet = new Set<CRMStatus>();
    filterCondensedStatuses.forEach(status => {
      switch (status) {
        case 'Research':
          statusSet.add(CRMStatus.RESEARCH);
          break;
        case 'Recommend Take Meeting':
          statusSet.add(CRMStatus.RECOMMEND_TAKE_MEETING);
          break;
        case 'Recommend Pass on Meeting':
          statusSet.add(CRMStatus.RECOMMEND_PASS_ON_MEETING);
          break;
        case 'Discussion':
          statusSet.add(CRMStatus.DISCUSSION);
          break;
        case 'Watchful Waiting':
          statusSet.add(CRMStatus.WATCHFUL_WAITING);
          break;
        case 'Recommend':
          statusSet.add(CRMStatus.RECOMMEND_INVEST_BUY);
          statusSet.add(CRMStatus.RECOMMEND_INVEST_SELL);
          statusSet.add(CRMStatus.RECOMMEND_INVEST_SHORT);
          statusSet.add(CRMStatus.RECOMMEND_INVEST_COVER);
          statusSet.add(CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION);
          statusSet.add(CRMStatus.RECOMMEND_NO_INVESTMENT);
          break;
        case 'DL Approves':
          statusSet.add(CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT);
          statusSet.add(CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_BUY);
          statusSet.add(CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SELL);
          statusSet.add(CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_COVER);
          statusSet.add(CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SHORT);
          statusSet.add(CRMStatus.DL_APPROVES_RECOMMEND_AWAITING_KEY_INFLECTION);
          statusSet.add(CRMStatus.DL_APPROVES_RECOMMEND_NO_INVESTMENT);
          break;
        case 'DL Rejects':
          statusSet.add(CRMStatus.DL_REJECTS_RECOMMEND_INVESTMENT);
          statusSet.add(CRMStatus.DL_REJECTS_RECOMMEND_AWAITING_KEY_INFLECTION);
          statusSet.add(CRMStatus.DL_REJECTS_RECOMMEND_NO_INVESTMENT);
          break;
        case 'PM Approves':
          statusSet.add(CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT);
          statusSet.add(CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_BUY);
          statusSet.add(CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SELL);
          statusSet.add(CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_COVER);
          statusSet.add(CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SHORT);
          break;
        case 'PM Rejects':
          statusSet.add(CRMStatus.PM_REJECTS_RECOMMEND_INVESTMENT);
          break;
        case 'Investment not made (not our choice)':
          statusSet.add(CRMStatus.INVESTMENT_NOT_MADE_NOT_OUR_CHOICE);
          break;
        case 'Investment not made (our choice)':
          statusSet.add(CRMStatus.INVESTMENT_NOT_MADE_OUR_CHOICE);
          break;
        case 'Investment not made (neutrally closed)':
          statusSet.add(CRMStatus.INVESTMENT_NOT_MADE_NEUTRALLY_CLOSED);
          break;
        case 'Investment not made (company cancelled deal)':
          statusSet.add(CRMStatus.INVESTMENT_NOT_MADE_COMPANY_CANCELLED);
          break;
        case 'Investment made (suboptimal)':
          statusSet.add(CRMStatus.INVESTMENT_MADE_SUBOPTIMAL);
          break;
        case 'Investment made (optimal)':
          statusSet.add(CRMStatus.INVESTMENT_MADE_OPTIMAL);
          break;
        case 'Closed Before Decision':
          statusSet.add(CRMStatus.CLOSED_BEFORE_DECISION);
          break;
        default:
          break;
      }
    });
    return Array.from(statusSet);
  }

  public getCurrentStage(crmItem: CRMItem): number {
    switch (crmItem.status) {
      case CRMStatus.RESEARCH:
      case CRMStatus.SILENCED:
      case CRMStatus.UNSILENCED:
      case CRMStatus.RECOMMEND_TAKE_MEETING:
      case CRMStatus.RECOMMEND_PASS_ON_MEETING:
        return 0;
      case CRMStatus.DISCUSSION:
        return 1;
      case CRMStatus.WATCHFUL_WAITING:
      case CRMStatus.RECOMMEND_NO_INVESTMENT:
      case CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION:
      case CRMStatus.RECOMMEND_INVEST_BUY:
      case CRMStatus.RECOMMEND_INVEST_SELL:
      case CRMStatus.RECOMMEND_INVEST_SHORT:
      case CRMStatus.RECOMMEND_INVEST_COVER:
        return 2;
      case CRMStatus.DL_APPROVES_RECOMMEND_AWAITING_KEY_INFLECTION:
      case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT:
      case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_BUY:
      case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SELL:
      case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_COVER:
      case CRMStatus.DL_APPROVES_RECOMMEND_INVESTMENT_SHORT:
      case CRMStatus.DL_APPROVES_RECOMMEND_NO_INVESTMENT:
      case CRMStatus.DL_REJECTS_RECOMMEND_AWAITING_KEY_INFLECTION:
      case CRMStatus.DL_REJECTS_RECOMMEND_INVESTMENT:
      case CRMStatus.DL_REJECTS_RECOMMEND_NO_INVESTMENT:
        return 3;
      case CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT:
      case CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_BUY:
      case CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SELL:
      case CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_COVER:
      case CRMStatus.PM_APPROVES_RECOMMEND_INVESTMENT_SHORT:
      case CRMStatus.PM_REJECTS_RECOMMEND_INVESTMENT:
        return 4;
      case CRMStatus.INVESTMENT_MADE_OPTIMAL:
      case CRMStatus.INVESTMENT_MADE_SUBOPTIMAL:
      case CRMStatus.INVESTMENT_NOT_MADE_NOT_OUR_CHOICE:
      case CRMStatus.INVESTMENT_NOT_MADE_OUR_CHOICE:
      case CRMStatus.INVESTMENT_NOT_MADE_NEUTRALLY_CLOSED:
      case CRMStatus.INVESTMENT_NOT_MADE_COMPANY_CANCELLED:
      case CRMStatus.CLOSED_BEFORE_DECISION:
        return 5;
    }
  }

  public getStages(crmItem: CRMItem): Array<string> {
    const stages = ['Research', 'Discussion', 'Recommendation', 'DLA', 'PMA', 'Outcome'];
    if (crmItem.status === CRMStatus.RECOMMEND_INVEST_BUY ||
      crmItem.status === CRMStatus.RECOMMEND_INVEST_SELL ||
      crmItem.status === CRMStatus.RECOMMEND_INVEST_SHORT ||
      crmItem.status === CRMStatus.RECOMMEND_INVEST_COVER ||
      crmItem.status === CRMStatus.RECOMMEND_NO_INVESTMENT ||
      crmItem.status === CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION) {
      stages[2] = CRMStatusLabels[crmItem.status];
    }
    if (crmItem.status === CRMStatus.WATCHFUL_WAITING) {
      stages.splice(2, 0, 'Watchful Waiting');
    }
    if (crmItem.status === CRMStatus.RECOMMEND_TAKE_MEETING || crmItem.status === CRMStatus.RECOMMEND_PASS_ON_MEETING) {
      stages.splice(0, 0, 'Triaged');
    }
    return stages;
  }

  public isRejected(crmItem: CRMItem): boolean {
    switch (crmItem.status) {
      case CRMStatus.DL_REJECTS_RECOMMEND_AWAITING_KEY_INFLECTION:
      case CRMStatus.DL_REJECTS_RECOMMEND_INVESTMENT:
      case CRMStatus.DL_REJECTS_RECOMMEND_NO_INVESTMENT:
      case CRMStatus.PM_REJECTS_RECOMMEND_INVESTMENT:
      case CRMStatus.CLOSED_BEFORE_DECISION:
      case CRMStatus.RECOMMEND_PASS_ON_MEETING:
        return true;
      default:
        return false;
    }
  }

  public isOptionalStatus(status: CRMStatus): boolean {
    return status === CRMStatus.WATCHFUL_WAITING || status === CRMStatus.CLOSED_BEFORE_DECISION;
  }

  public loadCrmStatusOrder(): Observable<any> {
    return super.getHTTP().get(`${this.getApiPath()}/order`)
    .pipe(catchError(ex => super.getException().catchBadResponse(ex)));
  }

  public hasEtakiStatus(item: {status: CRMStatus | string}): boolean {
    return [
      CRMStatus.DL_APPROVES_RECOMMEND_AWAITING_KEY_INFLECTION,
      CRMStatus.DL_REJECTS_RECOMMEND_AWAITING_KEY_INFLECTION,
      CRMStatus.RECOMMEND_AWAITING_KEY_INFLECTION,
    ].some(status => status === item.status);
  }

}
